import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Compass, Building2, Heart, Lightbulb, Briefcase, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";

export default function Footer() {
  const nav = useNavigate();
  const year = new Date().getFullYear();
  // Accordéons : uniquement actifs en version mobile (CSS). Tout reste déplié sur desktop.
  const [open, setOpen] = useState({});
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const Chev = () => <ChevronDown size={18} className="footer-col-chev" />;
  const gridRef = useRef(null);

  // Referme les accordéons ouverts dès qu'on clique en dehors de la grille du footer.
  useEffect(() => {
    const hasOpen = Object.values(open).some(Boolean);
    if (!hasOpen) return;
    const onDown = (e) => {
      if (gridRef.current && !gridRef.current.contains(e.target)) {
        setOpen({});
      }
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  return (
    <footer className="site-footer" data-testid="site-footer">
      <a className="footer-wa-cta" href="https://wa.me/33762444417?text=Bonjour%20AfriFut%C3%A9%2C%20j%27aimerais%20%C3%A9changer%20avec%20vous." target="_blank" rel="noreferrer" data-testid="footer-wa-cta">
        <span className="footer-wa-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 00-8.6 15l-1.3 4.8 4.9-1.3A10 10 0 1012 2zm4.5 13.6c-.2-.1-1.4-.7-1.6-.8s-.4-.1-.5.1l-.7.9c-.1.2-.3.2-.5.1a6.5 6.5 0 01-3.2-2.8c-.2-.4.2-.4.6-1.2.1-.1 0-.3 0-.4l-.7-1.7c-.2-.5-.4-.4-.5-.4h-.5c-.2 0-.4.1-.6.3-.7.7-.9 1.6-.6 2.8.5 1.6 1.6 3 3.2 4 2.2 1.3 3.1 1 3.7.9.5-.1 1.4-.6 1.6-1.1.2-.5.2-1 .1-1.1z"/></svg>
        </span>
        <span className="footer-wa-text">
          <b>Parlons de votre projet</b>
          <span>Une question, une solution à référencer ? Écrivez-nous sur WhatsApp.</span>
        </span>
        <span className="footer-wa-btn">Nous écrire</span>
      </a>
      <div className="footer-grid" ref={gridRef}>
        <div className={`footer-col footer-brand ${open.brand ? "open" : ""}`}>
          <button type="button" className="footer-col-head" onClick={() => toggle("brand")} aria-expanded={!!open.brand}>
            <Logo height={30} showAtlas={false} />
            <Chev />
          </button>
          <div className="footer-col-body">
            <div className="footer-accroche">AfriFuté rend les solutions africaines</div>
            <div className="footer-tagline" style={{ textTransform: "capitalize" }}>
              <span className="ft-word">utile<span className="ft-sep">·</span><span className="ft-word ft-gold">concret</span><span className="ft-sep">·</span>actionnable</span>
            </div>
            <div className="footer-col footer-col-afrifute">
              <button onClick={() => nav("/mentions-legales")} data-testid="footer-mentions-legales">Mentions légales</button>
              <button onClick={() => nav("/confidentialite")} data-testid="footer-confidentialite">Confidentialité</button>
              <button onClick={() => nav("/a-propos")} data-testid="footer-a-propos">À propos</button>
              <button onClick={() => nav("/cgu")} data-testid="footer-cgu">CGU</button>
              {/* CGV masqué temporairement à la demande du client */}
              {/* <button onClick={() => nav("/cgv")} data-testid="footer-cgv">CGV</button> */}
            </div>
          </div>
        </div>

        <div className={`footer-col ${open.atlas ? "open" : ""}`}>
          <button type="button" className="footer-col-head" onClick={() => toggle("atlas")} aria-expanded={!!open.atlas}>
            <Logo height={30} showAtlas={true} />
            <Chev />
          </button>
          <div className="footer-col-body">
            <button onClick={() => nav("/solutions")} data-testid="footer-catalogue"><Compass size={14} /> Catalogue des solutions</button>
            <button onClick={() => nav("/services")} data-testid="footer-services"><Briefcase size={14} /> Nos services</button>
            <button onClick={() => nav("/espace-entreprise")}><Building2 size={14} /> Soumettre une solution</button>
            <button onClick={() => nav("/idees")} data-testid="footer-idees"><Lightbulb size={14} /> Idées de solutions</button>
            <button onClick={() => nav("/favoris")} data-testid="footer-favoris"><Heart size={14} /> Mes favoris</button>
          </div>
        </div>

        <div className={`footer-col ${open.edito ? "open" : ""}`}>
          <button type="button" className="footer-col-head" onClick={() => toggle("edito")} aria-expanded={!!open.edito}>
            <Logo height={30} showAtlas={true} label="Éditorial" />
            <Chev />
          </button>
          <div className="footer-col-body">
            <button onClick={() => nav("/articles?cat=opportunites")}>Opportunités</button>
            <button onClick={() => nav("/articles?cat=evenements")}>Événements</button>
            <button onClick={() => nav("/articles?cat=entreprises")}>Entreprises</button>
            <button onClick={() => nav("/articles?cat=tendance")}>Tendances</button>
            <button onClick={() => nav("/articles?cat=diaspora")}>Diaspora</button>
            <button onClick={() => nav("/articles?cat=outils")}>Outils</button>
          </div>
        </div>

        <div className={`footer-col footer-col-canaux ${open.canaux ? "open" : ""}`}>
          <button type="button" className="footer-col-head" onClick={() => toggle("canaux")} aria-expanded={!!open.canaux}>
            <Logo height={30} showAtlas={true} label="Canaux" />
            <Chev />
          </button>
          <div className="footer-col-body">
            <a href="https://wa.me/33762444417" target="_blank" rel="noreferrer" aria-label="WhatsApp">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 00-8.6 15l-1.3 4.8 4.9-1.3A10 10 0 1012 2zm4.5 13.6c-.2-.1-1.4-.7-1.6-.8s-.4-.1-.5.1l-.7.9c-.1.2-.3.2-.5.1a6.5 6.5 0 01-3.2-2.8c-.2-.4.2-.4.6-1.2.1-.1 0-.3 0-.4l-.7-1.7c-.2-.5-.4-.4-.5-.4h-.5c-.2 0-.4.1-.6.3-.7.7-.9 1.6-.6 2.8.5 1.6 1.6 3 3.2 4 2.2 1.3 3.1 1 3.7.9.5-.1 1.4-.6 1.6-1.1.2-.5.2-1 .1-1.1z"/></svg> WhatsApp
            </a>
            <a href="https://www.instagram.com/afrifute" target="_blank" rel="noreferrer" aria-label="Instagram">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/></svg> Instagram
            </a>
            <a href="https://www.facebook.com/AfriFute" target="_blank" rel="noreferrer" aria-label="Facebook">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12a10 10 0 10-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0022 12z"/></svg> Facebook
            </a>
            <a href="https://www.linkedin.com/company/109762687/" target="_blank" rel="noreferrer" aria-label="LinkedIn">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M4.98 3.5A2.5 2.5 0 002.5 6a2.5 2.5 0 105 0 2.5 2.5 0 00-2.52-2.5zM3 8.98h4V21H3zM9 8.98h3.8v1.64h.05c.53-1 1.83-2.05 3.76-2.05 4 0 4.76 2.64 4.76 6.08V21h-4v-5.36c0-1.28 0-2.92-1.78-2.92s-2.05 1.4-2.05 2.83V21H9z" /></svg> LinkedIn
            </a>
            <a href="mailto:contact@afrifute.com" aria-label="Email">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 6-10 7L2 6"/></svg> Email
            </a>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <span>© {year} AfriFuté - Tous droits réservés.</span>
      </div>
    </footer>
  );
}
